import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { logError } from 'utils/logging';
import { parseJSONScalar } from 'utils/parseJsonScalar';
import { isNull, isUndefined } from 'typeDeclarations/typeGuards';
import { useWindowEvent } from './useWindowEvent';

interface UseLocalStorageArgs<T> {
  key: string;
  initialValue: T;
  // needed to overcome the issue of obsolete stored values
  reconcileStoredWithInitialValue?: (storedValue: T) => T;
}

export const useLocalStorage = <T>({
  key,
  initialValue,
  reconcileStoredWithInitialValue,
}: UseLocalStorageArgs<T>): [T, Dispatch<SetStateAction<T>>] => {
  const eventName = 'leadzai-local-storage';
  const isUpdatingRef = useRef(false);
  const lastStoredValueRef = useRef<string | null>(null);

  interface CustomEventDetail {
    value: T;
    key: string;
  }

  const [state, setState] = useState<T>(() => {
    try {
      const storedObject = localStorage.getItem(key);

      if (isNull(storedObject)) {
        return initialValue;
      }

      const parsedStoredValue = parseJSONScalar<T>(storedObject);

      if (isUndefined(parsedStoredValue)) {
        return initialValue;
      }

      return reconcileStoredWithInitialValue?.(parsedStoredValue) ?? parsedStoredValue;
    } catch (error) {
      return initialValue;
    }
  });

  // Subscribes to changes between different document
  useWindowEvent('storage', (event) => {
    if (event.key === key) {
      const parsedStoredValue = parseJSONScalar<T>(event.newValue) ?? initialValue;
      setState(parsedStoredValue);
    }
  });

  // Subscribes to changes on the same document
  useWindowEvent(eventName, (event) => {
    const customEvent = event as CustomEvent<CustomEventDetail>;
    if (customEvent.detail.key === key && !isUpdatingRef.current) {
      setState(customEvent.detail.value);
    }
  });

  // Store value in localStorage when state changes
  const storeValue = useCallback(
    (value: T) => {
      try {
        const serializedValue = JSON.stringify(value);
        // Only update if the value has actually changed
        if (lastStoredValueRef.current !== serializedValue) {
          localStorage.setItem(key, serializedValue);
          lastStoredValueRef.current = serializedValue;
          // Publishes the event to all subscribers
          dispatchEvent(new CustomEvent<CustomEventDetail>(eventName, { detail: { key, value } }));
        }
      } catch (error) {
        logError(new Error(`Something failed when trying to store the key '${key}' into local storage`));
      }
    },
    [key, eventName],
  );

  // Triggers when the state is updated and stores the value in the local storage
  useEffect(() => {
    if (!isUpdatingRef.current) {
      isUpdatingRef.current = true;
      storeValue(state);
      isUpdatingRef.current = false;
    }
  }, [state, storeValue]);

  return [state, setState];
};
